/**
 * Programmatic-SEO landing page — `/find-pros/cleared/:stateSlug`
 *
 * Why: long-tail queries like "background-checked dog walkers california"
 * are extremely high-intent. Buyers searching this are already past the
 * "do I trust dog walkers in general" stage and looking for a vetted
 * shortlist. We surface exactly that: every approved provider in the
 * target state whose Checkr background screen came back clear.
 *
 * Page surfaces:
 *  - Hero: "Background-check-cleared dog walkers in California (12 vetted)"
 *  - 3-tile trust strip explaining what "cleared" means + venue posture
 *  - Per-kind subcount strip (5 walkers · 4 sitters · 3 trainers)
 *  - Provider grid (reuses the FindPros card visual language with cleared lift)
 *  - JSON-LD ItemList for rich-result eligibility
 *  - Canonical link tag + OG meta
 *  - Footer venue disclosure
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { publicFileUrl } from "@/lib/utils-app";
import AppShell from "@/components/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import StarRating from "@/components/StarRating";
import { ShieldCheckIcon as ShieldCheck, GraduationCapIcon as GraduationCap, ScissorsIcon as Scissors, FootprintsIcon as Footprints, HouseIcon as House, MapPinIcon as MapPin, ArrowRightIcon as ArrowRight, SparkleIcon as Sparkle, TreeIcon as Tree } from "@phosphor-icons/react";

const KIND_META = {
  trainer: { label: "Trainers", Icon: GraduationCap, accent: "#0E0E0E" },
  groomer: { label: "Groomers", Icon: Scissors, accent: "#C9A227" },
  walker: { label: "Walkers", Icon: Footprints, accent: "#0F8043" },
  sitter: { label: "Sitters", Icon: House, accent: "#C1272D" },
  field_host: { label: "Zoomie Time", Icon: Tree, accent: "#2E6B3E" },
  other: { label: "Other Specialists", Icon: Sparkle, accent: "#3A6EA5" },
};

export default function ClearedProsByState() {
  const { stateSlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/public/cleared-pros/${stateSlug}`, { silent: true })
      .then((r) => setData(r.data || {}))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else setData({ providers: [], state: "", total: 0, kind_counts: {} });
      });
  }, [stateSlug]);

  if (notFound) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="cleared-pros-404">
          <h1 className="font-spectral text-4xl mb-3">Page not found</h1>
          <p className="text-[#6B6B6B] mb-6">
            That state slug isn&rsquo;t recognized. Try the full directory.
          </p>
          <Link to="/find-pros" className="text-crimson font-bold underline">
            Browse all providers →
          </Link>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-16 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-72" />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  const { state, state_code, providers = [], total = 0, kind_counts = {} } = data;
  const title =
    total > 0
      ? `Background-check-cleared dog walkers, sitters & trainers in ${state} (${total} vetted) | Armstrong K9 Registry`
      : `Background-check-cleared providers in ${state} | Armstrong K9 Registry`;
  const description =
    total > 0
      ? `${total} independent dog walkers, sitters, trainers and groomers in ${state} who have passed an FCRA-compliant criminal-record screen via our third-party vendor. Insurance status, ratings, and pricing on every listing.`
      : `Browse FCRA-screened, independent dog-care providers in ${state}. Insurance status, ratings, and pricing transparency on every listing.`;
  const canonical =
    typeof window !== "undefined"
      ? `${webOrigin()}/find-pros/cleared/${stateSlug}`
      : `/find-pros/cleared/${stateSlug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: providers.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.business_name,
      url: `${canonical.split("/find-pros")[0]}/provider/${p.slug}`,
    })),
    numberOfItems: total,
  };

  // Build the per-kind subcount strip dynamically so we never show "0 walkers".
  const subcounts = Object.entries(kind_counts)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => {
      const meta = KIND_META[k] || { label: k };
      const label = n === 1 ? meta.label.replace(/s$/, "") : meta.label;
      return `${n} ${label.toLowerCase()}`;
    });

  return (
    <AppShell>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div
        className="max-w-6xl mx-auto px-4 py-10"
        data-testid="cleared-pros-page"
      >
        {/* Hero */}
        <header className="mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#0F8043] font-bold mb-2 inline-flex items-center gap-1.5">
            <ShieldCheck size={11} weight="fill" />
            {state} · {state_code} · Background-check cleared
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Vetted dog-care providers in {state}
          </h1>
          <p className="text-base sm:text-lg text-[#3F3F3F] mt-3 max-w-3xl leading-relaxed">
            {total > 0 ? (
              <>
                <strong className="text-ink">{total}</strong> independent
                provider{total === 1 ? "" : "s"} in {state} who have passed an
                FCRA-compliant criminal-record screen via our third-party
                vendor.{" "}
                {subcounts.length > 0 && (
                  <span className="text-[#6B6B6B]">({subcounts.join(" · ")})</span>
                )}
              </>
            ) : (
              <>
                No background-check-cleared providers in {state} yet — be the
                first to list.
              </>
            )}
          </p>
        </header>

        {/* Trust strip */}
        <div className="grid sm:grid-cols-3 gap-3 mb-10">
          <TrustTile
            icon={<ShieldCheck size={20} weight="duotone" />}
            title="FCRA-compliant screen"
            blurb="Run by an independent third-party vendor, not by us. Adverse-action procedures follow federal law."
          />
          <TrustTile
            icon={<ShieldCheck size={20} weight="duotone" />}
            title="Insurance status visible"
            blurb="Every provider's coverage status is disclosed on their profile — verified, self-reported, or none."
          />
          <TrustTile
            icon={<MapPin size={20} weight="duotone" />}
            title="Independent businesses"
            blurb="Each provider runs their own business. We're the venue connecting you. You contract directly with them."
          />
        </div>

        {/* Provider grid */}
        {total > 0 ? (
          <section className="mb-12">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-spectral text-2xl">
                Cleared providers in {state}
              </h2>
              <Link
                to="/find-pros"
                className="text-[12px] text-crimson font-bold hover:underline inline-flex items-center gap-1"
                data-testid="see-all-link"
              >
                See all providers <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {providers.map((p) => (
                <ProviderCard key={p.id} p={p} />
              ))}
            </div>
          </section>
        ) : (
          <section className="mb-12 text-center py-10 bg-white border border-[#EAEAEA] rounded-xl">
            <p className="text-[#6B6B6B] mb-4">
              We haven&rsquo;t cleared a provider in {state} yet. The directory
              is growing — check the full national listing.
            </p>
            <Link
              to="/find-pros"
              className="inline-flex items-center gap-2 bg-ink hover:bg-crimson text-white rounded-full px-5 py-3 font-bold transition-colors"
              data-testid="empty-cta"
            >
              Browse all providers
              <ArrowRight size={14} weight="bold" />
            </Link>
          </section>
        )}

        {/* What "cleared" means — SEO content + legal clarity */}
        <section className="mb-10 bg-cream/40 border border-[#E2DAC2] rounded-2xl p-6">
          <h2 className="font-spectral text-2xl mb-3">
            What &ldquo;background-check cleared&rdquo; means
          </h2>
          <div className="text-[14px] text-[#3F3F3F] leading-relaxed space-y-3">
            <p>
              Every provider with the <strong>Cleared</strong> ribbon on this
              page has passed a Fair Credit Reporting Act (FCRA)-compliant
              criminal record screen run by an independent, accredited
              third-party vendor. The screen covers a national sex-offender
              registry, federal criminal records, and county-level criminal
              records in jurisdictions the candidate has lived in.
            </p>
            <p>
              Armstrong K9 Registry does <strong>not</strong> conduct or
              adjudicate background checks ourselves &mdash; we receive a
              cleared / consider / suspended result from the vendor and surface
              it on the provider&rsquo;s public profile. Providers in the
              &ldquo;consider&rdquo; bucket are <em>not</em> shown on this
              page; their cases are reviewed under FCRA adverse-action
              procedures before any listing decision is finalized.
            </p>
            <p>
              A cleared background check is one trust signal among many.
              Always read provider reviews, check insurance status, and meet
              the provider in person before allowing them unsupervised access
              to your dog.
            </p>
          </div>
        </section>

        {/* Venue disclosure footer */}
        <div className="mt-8 pt-6 border-t border-[#EAEAEA] text-[12px] text-[#6B6B6B] max-w-3xl leading-relaxed">
          <strong className="text-ink">Venue disclosure:</strong> Armstrong K9
          Registry connects customers with independent service providers and
          is not a party to any service transaction. Providers &mdash; not
          Armstrong K9 Registry &mdash; are responsible for performing their
          services and for carrying whatever insurance and licensing their
          jurisdiction or business requires. The background-check status
          reflects the result of an independent third-party screening at the
          time it was performed and does not guarantee future conduct.
        </div>
      </div>
    </AppShell>
  );
}

function TrustTile({ icon, title, blurb }) {
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-4">
      <div className="text-gold mb-2">{icon}</div>
      <p className="font-bold text-ink text-[13.5px]">{title}</p>
      <p className="text-[12px] text-[#6B6B6B] mt-1 leading-relaxed">{blurb}</p>
    </div>
  );
}

function ProviderCard({ p }) {
  const meta = KIND_META[p.kind] || { label: p.kind, Icon: GraduationCap };
  const Icon = meta.Icon;
  return (
    <Link
      to={`/provider/${p.slug}`}
      className="bg-white rounded-xl overflow-hidden hover:-translate-y-[2px] hover:shadow-gold-glow transition-all relative border-2 border-[#0F8043]/40 hover:border-[#0F8043]/70"
      data-testid={`cleared-provider-card-${p.id}`}
    >
      <div
        className="absolute top-0 right-0 z-10 bg-[#0F8043] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-bl-lg flex items-center gap-1"
      >
        <ShieldCheck size={10} weight="fill" /> Cleared
      </div>
      <div className="aspect-[16/10] bg-[#FAFAFA] relative">
        {p.photo_url ? (
          <img
            src={publicFileUrl(p.photo_url)}
            alt={p.business_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon size={48} weight="duotone" className="text-[#C4C4C4]" />
          </div>
        )}
        <span
          className="absolute top-3 left-3 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-white/95 backdrop-blur-sm flex items-center gap-1"
          style={{ color: meta.accent }}
        >
          <Icon size={10} weight="duotone" />
          {p.specialty_label || meta.label.replace(/s$/, "")}
        </span>
        {p.insurance_verified && (
          <span className="absolute top-12 right-3 text-[9.5px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#0F8043]/95 text-white flex items-center gap-1">
            <ShieldCheck size={10} weight="fill" /> Verified
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="font-spectral text-lg leading-tight text-ink">
          {p.business_name}
        </p>
        {p.tagline && (
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 line-clamp-2 leading-snug">
            {p.tagline}
          </p>
        )}
        {p.rating?.count > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-[11.5px] text-[#6B6B6B]">
            <StarRating value={p.rating.avg} size={11} />
            <span className="font-tabular font-semibold text-ink">
              {p.rating.avg.toFixed(1)}
            </span>
            <span>· {p.rating.count}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 mt-3 text-[11.5px] text-[#6B6B6B]">
          <MapPin size={11} weight="duotone" />
          {p.location?.city}, {p.location?.state}
        </div>
      </div>
    </Link>
  );
}
